import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, Square, UserCheck, Eye } from "lucide-react";
import type { Performer } from "@shared/schema";
import type { ParticipantMode } from "./mode-config";

interface ParticipantActionsProps {
  performer: Performer;
  mode: ParticipantMode;
  onActionToggle: (performer: Performer) => void;
  onConsider?: (performer: Performer) => void;
  disabled?: boolean;
  isLoading: boolean;
  isDragging?: boolean;
}

export function ParticipantActions({
  performer,
  mode,
  onActionToggle,
  onConsider,
  disabled = false,
  isLoading,
  isDragging = false,
}: ParticipantActionsProps) {
  const isJamNight = mode === 'jam-night';
  const isOpenMic = mode === 'open-mic';
  const isGenericEvent = mode === 'generic-event';

  const buttonStyle = isDragging ? {} : {
    boxShadow: '0 4px 16px rgba(34,197,94,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
  };

  const buttonClassName = isDragging 
    ? "bg-green-600 hover:bg-green-700 text-white border border-green-500 shadow-lg" 
    : "bg-green-600/80 backdrop-blur-sm text-white hover:bg-green-700/90 border border-green-500/30 shadow-lg transition-all duration-300";

  if (isGenericEvent) {
    return (
      <Button
        variant="default"
        size="sm"
        onClick={() => onActionToggle(performer)}
        disabled={disabled || isLoading}
        className={`${buttonClassName} disabled:opacity-50 disabled:cursor-not-allowed`}
        style={buttonStyle}
      >
        <UserCheck className="h-3 w-3 mr-1" />
        Mark Attended
      </Button>
    );
  }

  // Jam Night or Open Mic
  if (performer.status === 'on_stage') {
    return (
      <>
        <Button
          size="sm"
          onClick={() => onActionToggle(performer)}
          disabled={disabled || isLoading}
          className="bg-red-500/80 backdrop-blur-sm text-white hover:bg-red-600/90 border border-red-400/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            boxShadow: '0 4px 16px rgba(220,38,38,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
          }}
        >
          <Square className="h-3 w-3 mr-1" />
          Off Stage
        </Button>
        <Badge className="bg-green-100/40 backdrop-blur-sm text-green-800 border-green-200/50 shadow-sm">
          <div className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></div>
          On Stage
        </Badge>
      </>
    );
  }

  return (
    <>
        {isJamNight && performer.status === 'participant' && onConsider && (
            <Button
                size="sm"
                onClick={() => onConsider(performer)}
                disabled={disabled || isLoading}
                className="bg-yellow-600/80 backdrop-blur-sm text-white hover:bg-yellow-700/90 border border-yellow-500/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                    boxShadow: '0 4px 16px rgba(251,191,36,0.15), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
            >
                <Eye className="h-3 w-3 mr-1" />
                Consider
            </Button>
        )}
      <Button
        size="sm"
        onClick={() => onActionToggle(performer)}
        disabled={disabled || isLoading}
        className={`${buttonClassName} disabled:opacity-50 disabled:cursor-not-allowed`}
        style={buttonStyle}
      >
        <Play className="h-3 w-3 mr-1" />
        {isJamNight ? 'Pick' : 'On Stage'}
      </Button>
      {/* Consider button for Jam Night participants */}

    </>
  );
}

interface MobileParticipantActionsProps {
  performer: Performer;
  mode: ParticipantMode;
  onActionToggle: (performer: Performer) => void;
  onConsider?: (performer: Performer) => void;
  disabled?: boolean;
  isLoading: boolean;
}

export function MobileParticipantActions({
  performer,
  mode,
  onActionToggle,
  onConsider,
  disabled = false,
  isLoading,
}: MobileParticipantActionsProps) {
  const isJamNight = mode === 'jam-night';
  const isGenericEvent = mode === 'generic-event';

  const primaryButtonStyle = {
    boxShadow: '0 4px 16px rgba(34,197,94,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
  };

  const secondaryButtonStyle = {
    boxShadow: '0 4px 16px rgba(220,38,38,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
  };

  const considerButtonStyle = {
    boxShadow: '0 4px 16px rgba(251,191,36,0.15), inset 0 1px 0 rgba(255,255,255,0.3)',
  };

  return (
    <div className="flex flex-col justify-between items-end gap-2 flex-shrink-0">
      {/* Primary Action Button */}
      <div className="flex flex-col gap-2">
        {performer.status === 'on_stage' ? (
          <Badge className="bg-green-100/40 backdrop-blur-sm text-green-800 border-green-200/50 shadow-sm">
            <div className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></div>
            On Stage
          </Badge>
        ) : (
          <>
            <Button
              size="sm"
              onClick={() => onActionToggle(performer)}
              disabled={disabled || isLoading}
              className="bg-green-600/80 backdrop-blur-sm text-white hover:bg-green-700/90 border border-green-500/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              style={primaryButtonStyle}
            >
              {isGenericEvent ? (
                <>
                  <UserCheck className="h-3 w-3 mr-1" />
                  Mark Attended
                </>
              ) : (
                <>
                  <Play className="h-3 w-3 mr-1" />
                  {mode === 'open-mic' ? 'On Stage' : 'Select'}
                </>
              )}
            </Button>
            {/* Consider button for Jam Night participants */}
            {isJamNight && performer.status === 'participant' && onConsider && (
              <Button
                size="sm"
                onClick={() => onConsider(performer)}
                disabled={disabled || isLoading}
                className="bg-yellow-600/80 backdrop-blur-sm text-white hover:bg-yellow-700/90 border border-yellow-500/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                style={considerButtonStyle}
              >
                <Eye className="h-3 w-3 mr-1" />
                Consider
              </Button>
            )}
          </>
        )}
      </div>

      {/* Secondary action (Off Stage for Jam Night when on stage) */}
      {isJamNight && performer.status === 'on_stage' && (
        <Button
          size="sm"
          onClick={() => onActionToggle(performer)}
          className="bg-red-500/80 backdrop-blur-sm text-white hover:bg-red-600/90 border border-red-400/30 shadow-lg transition-all duration-300"
          style={secondaryButtonStyle}
        >
          <Square className="h-3 w-3 mr-1" />
          Off Stage
        </Button>
      )}
    </div>
  );
}